import { useState, type ReactNode } from 'react';
import { AppBar, Box, ButtonBase, InputAdornment, Stack, TextField, Toolbar } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { BrandLockup } from '../BrandMark';
import { UserMenu } from '../UserMenu';
import { NotificationsBell } from '../NotificationsBell';
import { navigate, paths } from '../../lib/router';

/** Portal-wide search box; Enter opens the search page. */
function PortalSearch({ initial }: { initial: string }) {
  const [q, setQ] = useState(initial);
  return (
    <Box
      component="form"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim().length >= 2) navigate(paths.search(q.trim()));
      }}
      sx={{ width: '100%', maxWidth: 420 }}
    >
      <TextField
        size="small"
        fullWidth
        placeholder="Search decks, announcements, documents"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        slotProps={{
          htmlInput: { 'aria-label': 'Search the portal', maxLength: 100 },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
}

/**
 * Header for portal pages (home, viewer): logo links home, page actions (or the portal search,
 * when `search` is given) in the middle, account menu right.
 */
export function PortalHeader({ children, search }: { children?: ReactNode; search?: string }) {
  return (
    <AppBar>
      <Toolbar sx={{ gap: 1.5, minHeight: { xs: 56 }, px: { xs: 2 } }}>
        <ButtonBase
          onClick={() => navigate(paths.home())}
          aria-label="Slide home"
          sx={{ borderRadius: 1, p: 0.5, m: -0.5 }}
        >
          <BrandLockup />
        </ButtonBase>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {children}
          {search !== undefined && <PortalSearch key={search} initial={search} />}
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <NotificationsBell />
          <UserMenu />
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
